// Agent Registry — Central definition of every agent in JARVIS V5
// Main Brain reads this to route tasks. Never hardcoded elsewhere.

export const NS = {
  TRADING:  'tr',
  TEACHING: 'te',
  CODING:   'co',
  PERSONAL: 'pe',
  SYSTEM:   'sy',
  TEMP:     'tmp',
};

// ─── J-AGENTS (Level 2 — Managers) ──────────────────────────────────────────

export const J_AGENTS = {

  J_TRADING: {
    id: 'J_TRADING', level: 2, type: 'J',
    name: 'J-Trading', icon: '📈', color: '#00d4ff',
    description: 'Trading Manager — coordinates all trading A-agents',
    namespace: NS.TRADING,
    a_agents: [
      'A_LIVE_PRICE','A_SESSION','A_NEWS',
      'A_FIBONACCI','A_STRUCTURE','A_LIQUIDITY',
      'A_PATTERN','A_RISK', // A_ICT removed — see note below (fabricated confidence, orphaned)
      'A_PSYCHOLOGY','A_STATISTICS','A_JOURNAL',
    ],
    // Keywords that trigger this J-Agent
    triggers: [
      'analyze','trade','price','xauusd','gold','buy','sell',
      'journal','risk','fibonacci','fib','ob','fvg','structure',
      'session','london','new york','asian','entry','stop loss',
      'target','market','chart','signal',
    ],
  },

  J_TEACHING: {
    id: 'J_TEACHING', level: 2, type: 'J',
    name: 'J-Teaching', icon: '📚', color: '#a78bfa',
    description: 'Teaching Manager — PDF, YouTube, MCQ, Evaluation',
    namespace: NS.TEACHING,
    a_agents: ['A_PDF','A_YOUTUBE','A_MCQ','A_EVALUATION'],
    triggers: ['learn','teach','pdf','youtube','quiz','mcq','knowledge','study','explain','concept'],
  },

  J_CODING: {
    id: 'J_CODING', level: 2, type: 'J',
    name: 'J-Coding', icon: '💻', color: '#34d399',
    description: 'Coding Manager — React, Node, Debug, Review',
    namespace: NS.CODING,
    a_agents: ['A_REACT','A_NODE','A_DEBUG','A_CODE_REVIEW'],
    triggers: ['code','debug','react','component','error','function','build','fix','node','javascript'],
  },

  J_MEMORY: {
    id: 'J_MEMORY', level: 2, type: 'J',
    name: 'J-Memory', icon: '💾', color: '#f472b6',
    description: 'Memory Manager — all memory namespaces',
    namespace: NS.PERSONAL,
    a_agents: ['A_LONG_TERM','A_PERSONAL'],
    triggers: ['remember','forget','memory','recall','save','what did'],
  },

  J_SYSTEM: {
    id: 'J_SYSTEM', level: 2, type: 'J',
    name: 'J-System', icon: '⚙️', color: '#94a3b8',
    description: 'System Manager — health, performance, agent status',
    namespace: NS.SYSTEM,
    a_agents: ['A_HEALTH'],
    triggers: ['status','health','performance','system','agents','how many'],
  },
};

// ─── A-AGENTS (Level 3 — Workers) ────────────────────────────────────────────

export const A_AGENTS = {

  // ── TRADING A-Agents ─────────────────────────────────────────────────────

  A_LIVE_PRICE: {
    id: 'A_LIVE_PRICE', level: 3, type: 'A',
    name: 'A-LivePrice', icon: '💰', color: '#10b981',
    parent: 'J_TRADING', namespace: NS.TRADING,
    requiresAI: false,
    cacheTTL:   30,       // seconds
    description: 'Live XAU/USD price from Yahoo Finance — no AI',
    inputs:  ['symbol'],
    outputs: ['price','change','changePct','high','low','open','volume'],
  },

  A_SESSION: {
    id: 'A_SESSION', level: 3, type: 'A',
    name: 'A-Session', icon: '🕐', color: '#6366f1',
    parent: 'J_TRADING', namespace: NS.TRADING,
    requiresAI: false,
    cacheTTL:   60,
    description: 'Detects current trading session — pure time logic, no AI',
    inputs:  [],
    outputs: ['session','bias','is_active','utc_hour','recommendation'],
  },

  A_NEWS: {
    id: 'A_NEWS', level: 3, type: 'A',
    name: 'A-News', icon: '📰', color: '#f59e0b',
    parent: 'J_TRADING', namespace: NS.TRADING,
    requiresAI: true,
    aiTokens:   250,      // light — sentiment JSON only
    cacheTTL:   1800,     // 30 minutes (news doesn't change every second)
    description: 'Macro sentiment for gold — Economic Calendar first, Groq if needed',
    inputs:  [],
    outputs: ['sentiment','strength','key_driver','bias','confidence','high_impact_today'],
  },

  A_FIBONACCI: {
    id: 'A_FIBONACCI', level: 3, type: 'A',
    name: 'A-Fibonacci', icon: '🔢', color: '#00d4ff',
    parent: 'J_TRADING', namespace: NS.TRADING,
    requiresAI: false,
    cacheTTL:   0,        // instant math, no cache needed
    description: 'Fibonacci retracement — pure math, Sir\'s personal levels, no AI',
    inputs:  ['high','low','direction'],
    outputs: ['levels','ote_zone','buy_level','sell_level1','sell_level2','recommendation'],
  },

  A_STRUCTURE: {
    id: 'A_STRUCTURE', level: 3, type: 'A',
    name: 'A-Structure', icon: '📐', color: '#8b5cf6',
    parent: 'J_TRADING', namespace: NS.TRADING,
    requiresAI: false,
    cacheTTL:   300,
    description: 'Market structure from OHLCV — HH/HL/LH/LL detection, no AI',
    inputs:  ['candles'],
    outputs: ['trend','last_bos','swing_high','swing_low','structure_bias'],
  },

  A_LIQUIDITY: {
    id: 'A_LIQUIDITY', level: 3, type: 'A',
    name: 'A-Liquidity', icon: '💧', color: '#06b6d4',
    parent: 'J_TRADING', namespace: NS.TRADING,
    requiresAI: false,
    cacheTTL:   300,
    description: 'Liquidity levels — equal highs/lows, swing points, no AI',
    inputs:  ['candles'],
    outputs: ['buy_side_liquidity','sell_side_liquidity','nearest_target','pools'],
  },

  A_PATTERN: {
    id: 'A_PATTERN', level: 3, type: 'A',
    name: 'A-Pattern', icon: '🕯️', color: '#ec4899',
    parent: 'J_TRADING', namespace: NS.TRADING,
    requiresAI: false,
    cacheTTL:   300,
    description: 'Candlestick & FVG pattern detection from OHLCV — no AI',
    inputs:  ['candles'],
    outputs: ['fvg_bullish','fvg_bearish','patterns','latest_signal'],
  },

  // A_ICT REMOVED (this session) — fabricated confidence + entry/SL/TP,
  // still referenced the old unvalidated 0.15/0.75/0.84/0.90 Fib levels and
  // the explicitly-unvalidated "OB Fib 0.5" strategy. Confirmed fully
  // orphaned (only reachable via JTrading.js → MainBrain.js, neither of
  // which anything else imported) — deleted rather than fixed, since
  // nothing live depended on it.

  A_RISK: {
    id: 'A_RISK', level: 3, type: 'A',
    name: 'A-Risk', icon: '🛡️', color: '#ef4444',
    parent: 'J_TRADING', namespace: NS.TRADING,
    requiresAI: false,
    cacheTTL:   0,
    description: 'Lot size, R:R, daily loss check — pure formula, no AI',
    inputs:  ['account_size','risk_pct','entry','sl','tp'],
    outputs: ['lot_size','risk_usd','rr_ratio','potential_profit','safe_to_trade','warnings'],
  },

  A_PSYCHOLOGY: {
    id: 'A_PSYCHOLOGY', level: 3, type: 'A',
    name: 'A-Psychology', icon: '🧘', color: '#a78bfa',
    parent: 'J_TRADING', namespace: NS.TRADING,
    requiresAI: false,   // rule-based first, Groq only if deep analysis requested
    cacheTTL:   3600,    // 1 hour
    description: 'FOMO/revenge/overtrading check — code rules, no AI for routine check',
    inputs:  ['recent_trades'],
    outputs: ['state','warnings','trade_allowed','advice'],
  },

  A_STATISTICS: {
    id: 'A_STATISTICS', level: 3, type: 'A',
    name: 'A-Statistics', icon: '📊', color: '#10b981',
    parent: 'J_TRADING', namespace: NS.TRADING,
    requiresAI: false,
    cacheTTL:   60,
    description: 'Win rate, P&L, session stats, streak — pure calculation, no AI',
    inputs:  ['period'],
    outputs: ['win_rate','total_pnl','best_session','streak','avg_rr','profit_factor'],
  },

  A_JOURNAL: {
    id: 'A_JOURNAL', level: 3, type: 'A',
    name: 'A-Journal', icon: '📓', color: '#6b7280',
    parent: 'J_TRADING', namespace: NS.TRADING,
    requiresAI: false,
    cacheTTL:   0,
    description: 'Trade journal CRUD — save, read, update — no AI',
    inputs:  ['action','trade_data'],
    outputs: ['trades','success','count'],
  },
};

// ─── All agents combined ──────────────────────────────────────────────────────
export const ALL_AGENTS = { ...J_AGENTS, ...A_AGENTS };

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Route user input to the right J-Agent
export function routeToJAgent(input = '') {
  const lower = input.toLowerCase();
  let best = null;
  let bestScore = 0;

  Object.values(J_AGENTS).forEach(j => {
    const score = (j.triggers || []).filter(t => lower.includes(t)).length;
    if (score > bestScore) { bestScore = score; best = j; }
  });

  return best || J_AGENTS.J_TRADING; // default: trading
}

// Get all A-agents for a J-agent
export function getChildAgents(jAgentId) {
  const j = J_AGENTS[jAgentId];
  if (!j) return [];
  return j.a_agents.map(id => A_AGENTS[id]).filter(Boolean);
}

// Registry statistics
export function getRegistryStats() {
  const jCount = Object.keys(J_AGENTS).length;
  const aCount = Object.keys(A_AGENTS).length;
  const noAI   = Object.values(A_AGENTS).filter(a => !a.requiresAI).length;
  const withAI = Object.values(A_AGENTS).filter(a =>  a.requiresAI).length;
  return { j_agents: jCount, a_agents: aCount, no_ai: noAI, needs_ai: withAI, total: jCount + aCount };
}
